import type { PropsWithChildren, ReactNode } from 'react'
import { ClockCircleOutlined, SafetyOutlined, ShoppingOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { Card, Flex, Grid, Space, Typography, theme } from 'antd'
import { useTranslation } from 'react-i18next'
import { BrandLogo } from '@/shared/ui/BrandLogo'
import { LanguageSwitcher } from '@/shared/ui/LanguageSwitcher'
import { NetworkBackdrop } from '@/shared/ui/NetworkBackdrop'
import { ThemeToggle } from '@/shared/ui/ThemeToggle'

interface AuthShellProps extends PropsWithChildren {
  title: string
  subtitle?: ReactNode
  width?: number
}

/**
 * Kirish ekrani uchun umumiy qobiq.
 *
 * <h2>⚠️ QAYTA ISHLANDI — nega</h2>
 * Ilgari bu bo'sh fon ustidagi bitta kichik karta edi. Texnik jihatdan
 * yetarli, lekin abonent uchun bu BIRINCHI va ko'pincha YAGONA ekran:
 * agar u ishonch uyg'otmasa, abonent parolini kiritmaydi va telefon
 * qilishda davom etadi — ya'ni kabinetning butun maqsadi bekor bo'ladi.
 *
 * <h2>⚠️ Kartalar admin panelidagidan BOSHQA — bu nusxa emas</h2>
 * Adminda ular xodimga qoidalarni aytadi (TOTP majburiy, har amal audit
 * qilinadi). Bu yerda kirayotgan odam — ABONENT, unga qoidalar emas,
 * kabinet nima berishi kerak: qolgan trafikni ko'rish, paketni o'zi olish,
 * ulanish tarixini tekshirish. Adminning matnini bu yerga ko'chirish
 * abonentga hech narsa demagan bo'lardi.
 *
 * <h2>⚠️ Fishing ogohlantirishi — bezak emas</h2>
 * Oxirgi qatordagi "xodimlarimiz parolni so'ramaydi" — kabinet paroli
 * o'g'irlanishining eng keng tarqalgan yo'liga qarshi yagona arzon
 * chora. U aynan parol kiritiladigan ekranda turishi kerak, "yordam"
 * bo'limida emas — u yerga hech kim kirmaydi.
 *
 * <h2>⚠️ Tor ekranda chap ustun umuman chizilmaydi</h2>
 * Abonentlarning ko'pchiligi telefondan kiradi. Matnni tepaga qo'yish
 * formani ekrandan pastga surib yuborardi va har kirishda aylantirish
 * kerak bo'lardi. Kirish ekranining vazifasi — kiritish.
 */
export function AuthShell({ title, subtitle, width = 380, children }: AuthShellProps) {
  const { token } = theme.useToken()
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const wide = screens.lg === true

  const benefits = [
    { icon: <ThunderboltOutlined />, key: 'balance' },
    { icon: <ShoppingOutlined />, key: 'selfServe' },
    { icon: <ClockCircleOutlined />, key: 'history' },
  ] as const

  return (
    <div style={{ minHeight: '100vh', display: 'flex', background: token.colorBgLayout }}>
      <Flex gap="small" style={{ position: 'absolute', top: 16, insetInlineEnd: 16, zIndex: 2 }}>
        <LanguageSwitcher />
        <ThemeToggle />
      </Flex>

      {wide ? (
        <div
          style={{
            position: 'relative',
            flex: '1 1 0',
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${token.colorBgContainer} 0%, ${token.colorBgLayout} 100%)`,
            borderInlineEnd: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <NetworkBackdrop />

          <Flex
            vertical
            justify="center"
            gap={28}
            style={{ position: 'relative', height: '100%', padding: '48px 56px', maxWidth: 620 }}
          >
            <div>
              <Typography.Title level={1} style={{ margin: 0 }}>
                <BrandLogo height={48} label={t('app.name')} />
              </Typography.Title>
              <Typography.Paragraph
                type="secondary"
                style={{ fontSize: 16, marginTop: 16, marginBottom: 0 }}
              >
                {t('auth.brandTagline')}
              </Typography.Paragraph>
            </div>

            <Space direction="vertical" size={12}>
              {benefits.map((b) => (
                <Card key={b.key} size="small" style={{ background: token.colorBgElevated }}>
                  <Flex gap={12} align="flex-start">
                    <span style={{ color: token.colorPrimary, fontSize: 18, lineHeight: '22px' }}>
                      {b.icon}
                    </span>
                    <div>
                      <Typography.Text strong>{t(`auth.benefit.${b.key}.title`)}</Typography.Text>
                      <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
                        {t(`auth.benefit.${b.key}.text`)}
                      </Typography.Paragraph>
                    </div>
                  </Flex>
                </Card>
              ))}
            </Space>
          </Flex>
        </div>
      ) : null}

      <Flex
        align="center"
        justify="center"
        style={{ flex: wide ? '0 0 clamp(400px, 32vw, 520px)' : '1 1 auto', padding: 24 }}
      >
        <div style={{ width: '100%', maxWidth: width }}>
          {/* Tor ekranda chap ustun yo'q — mahsulot nomi shu yerda qoladi,
              aks holda abonent qaysi tizimga kirayotganini bilmasdi */}
          {!wide ? (
            <Flex vertical align="center" gap={10} style={{ marginBottom: 20 }}>
              <Typography.Title level={1} style={{ margin: 0 }}>
                <BrandLogo height={36} label={t('app.name')} />
              </Typography.Title>
              <Typography.Text type="secondary">{t('app.tagline')}</Typography.Text>
            </Flex>
          ) : null}

          <Card>
            <Flex vertical gap="large">
              <Flex vertical align="center" gap={4} style={{ textAlign: 'center' }}>
                <Typography.Title level={3} style={{ marginBottom: 0 }}>
                  {title}
                </Typography.Title>
                {subtitle ? <Typography.Text type="secondary">{subtitle}</Typography.Text> : null}
              </Flex>

              {children}
            </Flex>
          </Card>

          <Flex gap={8} align="flex-start" style={{ marginTop: 16, padding: '0 4px' }}>
            <SafetyOutlined style={{ color: token.colorTextTertiary, marginTop: 3 }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('auth.phishingNotice')}
            </Typography.Text>
          </Flex>
        </div>
      </Flex>
    </div>
  )
}
